<script lang="ts" setup>
import {menuMainItems} from "~~/route/route.config";
import type {MenuItemRoute} from "~~/route/interface/menu-item-route.interface";
import {useI18nCollection} from "~/composables/useI18nCollection";
import {useSiteLayout} from "~/composables/useSiteLayout";

const {t, locale} = useI18n();
const {pageUri} = useI18nCollection();
const {mobileMenuOpen, isMobileUi} = useSiteLayout();
const {currentRoute} = useRouter();
const nuxtApp = useNuxtApp();
const isNavigating = ref(false);
const hydrationReady = useState<boolean>('hydration-ready', () => false);
const disableNav = computed(() => hydrationReady.value && isNavigating.value);
nuxtApp.hook('page:start', () => {
    isNavigating.value = true;
});
nuxtApp.hook('page:finish', () => {
    isNavigating.value = false;
});
const navItems: MenuItemRoute[] = menuMainItems.filter(item => !item?.secondary)
const itemClass: Record<string, string> & { default: string, default_no_active: string, default_active: string } = {
    'pro': 'text-2xl laptop:text-xs desktop:text-base  px-4 laptop:px-3 desktop:px-4 py-2 laptop:rounded-full',
    'pro_no_active': 'laptop:text-grey-dark laptop:border-brand laptop:border laptop:bg-white laptop:hover:bg-brand laptop:hover:text-light',
    'pro_active': 'text-brand laptop:bg-dark laptop:text-light laptop:hover:bg-light laptop:hover:text-dark',
    'contact': 'text-2xl laptop:text-xs desktop:text-base  px-4 laptop:px-3 desktop:px-4 py-2 laptop:rounded-full',
    'contact_no_active': 'laptop:text-white laptop:bg-brand laptop:hover:bg-brand-dark laptop:hover:text-light',
    'contact_active': 'text-brand laptop:bg-dark laptop:text-light laptop:hover:bg-light laptop:hover:text-dark',
    'default': 'text-2xl laptop:text-xs desktop:text-base px-4 laptop:px-3 desktop:px-4 py-2 inline-block',
    'default_no_active': 'text-dark laptop:text-grey hover:text-brand',
    'default_active': 'text-brand hover:text-brand-dark',
}
useHead({
    bodyAttrs: {
        class: computed(() => mobileMenuOpen.value && isMobileUi.value ? 'overflow-hidden' : ''),
    },
});
</script>

<template>
  <Transition name="slide">
    <nav
            v-show="mobileMenuOpen"
            class="nav bg-light absolute inset-0 laptop:static laptop:bg-white z-10 flex flex-col laptop:flex-row flex-nowrap laptop:items-center justify-center laptop:justify-end transition-all origin-right header-sticky__nav">
      <ul class="mb-3 tablet:mb-0 flex flex-col items-start laptop:items-center laptop:flex-row flex-nowrap justify-center laptop:justify-start mr-3 gap-3 laptop:gap-2 desktop:gap-3">
        <li
                v-for="item in navItems" :key="item.identifier"
                class="mb-1 laptop:mb-0">
          <SiteHydrationSafeLink
                  :class="[
                          itemClass?.[item.identifier] || itemClass.default,
                          {
                            [itemClass?.[`${item.identifier}_active`] || itemClass.default_active]: currentRoute.fullPath === pageUri(item.path[locale]),
                            [itemClass?.[`${item.identifier}_no_active`] || itemClass.default_no_active]: currentRoute.fullPath !== pageUri(item.path[locale]),
                            'opacity-50 pointer-events-none': disableNav
                          }
                          ]"
                  :to="pageUri(item.path[locale])"
                  class="transition-all px-4 py-2 font-medium inline-block header-sticky__nav-link"
                  @click="mobileMenuOpen = false"
          >
            {{ t(`nav.${item.identifier}`) }}
          </SiteHydrationSafeLink>
        </li>
      </ul>
      <SiteLanguageSwitcher/>
    </nav>
  </Transition>
</template>
<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: opacity .2s ease, transform .1s ease
}

.slide-enter-from {
    transform: translateY(-50%);
    opacity: 0;
}

.slide-enter-to {
    transform: translateY(0);
    opacity: 1;
}

@media (min-width: 992px) {
    .nav {
        display: flex !important;
    }
}
</style>
